import { useEffect, useReducer, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import GridItemContainer from '../../componentes/form-grids/GridItemContainer';
import CustomAlert from '../../componentes/alerts/CustomAlert';
import GridInputRow from '../../componentes/form-grids/GridInputRow';
import { Grid } from '@material-ui/core';
import { DefaultScreenAccordion } from "../../componentes/layouts/components/DefaultScreenAccordion";
import { DefaultAutocomplete } from '../../componentes/autocomplete/DefaultAutocomplete';
import { IconLabelSwitch } from "../../componentes/iconSwitch/IconLabelSwitch";
import LabelScanningService from "./LabelScanningService";
import InputField from "../../componentes/form-fields/InputField/InputField";
import DefaultButton from "../../componentes/button/DefaultButton";
import { PrinterClientAutocomplete } from "../../componentes/autocomplete/PrinterClientAutocomplete";
import { DialogConformationGeneric } from "../../componentes/dialog/DialogConfirmationGeneric";

export default function LabelScanningMotul() {

    const { t } = useTranslation();
    const { control } = useForm();
    const [alert, setAlert] = useState(false);

    const userId = sessionStorage.getItem("userId");

    const initialState = {
        userId: userId ? Number(userId) : 0,
        idProductMotul: 0,
        idClient: 8,
        idLayoutEtiqueta: 0,
        labelQuantity: 0,
        idPrinter: 0,
        productName: "",
        productCode: "",
        labelInformation: "",
        label: "",
        status: 1,
        productCodeEAN: ""
    };

    const reducer = (state, action) => {
        switch (action.type) {
            case 'update':
                return { ...state, ...action.data };

            case "clear":
                return { ...initialState };

            case "clearProduct":
                return {
                    ...state,
                    idProductMotul: 0,
                    productName: "",
                    productCode: "",
                    labelInformation: "",
                    label: "",
                    productCodeEAN: action.keepEan ? action.ean : ""
                };

            case 'success':
                return { ...state };

            case 'error':
                return { ...state };

            case 'close_alert':
                return { ...state };

            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);
    const [message, setMessage] = useState("");
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warning, setWarning] = useState(false);
    const lastProcessedEanRef = useRef("");

    const getAdditionalPropertyValue = (item, propertyName) => {
        return item?.aditionalPropertiesList
            ?.find(x => x.name?.toLowerCase() === propertyName.toLowerCase())?.value;
    };

    const resetAlerts = () => {
        setError(false);
        setSuccess(false);
        setWarning(false);
    };

    const clearSelectedProduct = (ean = "") => {
        dispatch({ type: "clearProduct", keepEan: !!ean, ean });
    };

    const applySelectedProduct = (product, fallbackName = "") => {
        const nextEan = product?.productCodeEAN ?? product?.productCodeEan ?? "";

        dispatch({
            type: "update",
            data: {
                idProductMotul: product?.idProduct ?? 0,
                productName: product?.productName ?? fallbackName,
                productCodeEAN: nextEan,
                labelInformation: product?.labelInformation ?? ""
            }
        });

        lastProcessedEanRef.current = nextEan;
    };

    const fillProductCodeEanById = (idProductMotul, fallbackName = "", fallbackEan = "") => {
        return LabelScanningService
            .getProductMotul({
                idProduct: idProductMotul,
                idClient: state.idClient,
                status: 1
            })
            .then((response) => {
                const product = response?.data?.[0];

                if (!product) {
                    throw new Error("Produto Motul nao encontrado na busca detalhada.");
                }

                applySelectedProduct(product, fallbackName);
                resetAlerts();
            })
            .catch(() => {
                dispatch({
                    type: "update",
                    data: {
                        idProductMotul,
                        productName: fallbackName,
                        productCodeEAN: fallbackEan,
                        labelInformation: ""
                    }
                });
            });
    };

    const handleChange = (event) => {
        const { name, value, valueComplete } = event.target;

        if (name === "idProductMotul") {
            resetAlerts();

            if (!value) {
                lastProcessedEanRef.current = "";
                clearSelectedProduct();
                return;
            }

            const fallbackEan = getAdditionalPropertyValue(valueComplete, "CodBarra") ?? "";

            dispatch({
                type: "update",
                data: {
                    idProductMotul: value,
                    productName: valueComplete?.name ?? "",
                    productCodeEAN: fallbackEan
                }
            });

            fillProductCodeEanById(value, valueComplete?.name ?? "", fallbackEan);


            const request = { idProduct: value };

            LabelScanningService
                .getProductMotulValidate(request)
                .then((response) => {
                    if (!response.data) {
                        setMessage(t("productMotulCannotBeLabeled"));
                        setAlert(true);
                    }
                })
                .catch((e) => {
                    //setAlert(true);
                });

            return;
        }

        if (name === "productCodeEAN") {
            const nextValue = value ?? "";

            dispatch({ type: "update", data: { productCodeEAN: nextValue } });

            if (!nextValue) {
                lastProcessedEanRef.current = "";
                clearSelectedProduct();
                resetAlerts();
                return;
            }

            if (state.idProductMotul > 0 && nextValue !== state.productCodeEAN) {
                clearSelectedProduct(nextValue);
            }

            const request = { productCodeEAN: nextValue };

            LabelScanningService
                .getProductMotulValidate(request)
                .then((response) => {
                    if (!response.data) {
                        setMessage(t("productMotulCannotBeLabeled"));
                        setAlert(true);
                    }
                })
                .catch((e) => {
                    //setAlert(true);
                });

            return;
        }

        dispatch({ type: 'update', data: { [name]: value } });
    };

    const handleSearchProductByEan = () => {
        const ean = state.productCodeEAN?.trim();

        if (!ean) {
            lastProcessedEanRef.current = "";
            clearSelectedProduct();
            return Promise.resolve();
        }

        if (ean === lastProcessedEanRef.current && state.idProductMotul > 0) {
            return Promise.resolve();
        }

        lastProcessedEanRef.current = ean;

        return LabelScanningService
            .getProductMotulByEAN(ean)
            .then((response) => {
                applySelectedProduct(response.data);
                dispatch({
                    type: "update",
                    data: {
                        productCodeEAN: response.data.productCodeEAN ?? ean
                    }
                });
                resetAlerts();
            })
            .catch((e) => {
                clearSelectedProduct(ean);
                setSuccess(false);
                setMessage(
                    e?.response?.status === 404
                        ? t("productMotulEanNotFound")
                        : t("failApi")
                );
                setWarning(true);
            });
    };

    const handleProductEanBlur = () => {
        handleSearchProductByEan();
    };

    const handleProductEanKeyPress = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleSearchProductByEan();
        }
    };

    const alertCallback = () => {
        setAlert(false);
        handleClearAll();
    };

    const handleCloseAlertProduct = () => {
        dispatch({ type: 'close_alert' });
        setError(false);
        setSuccess(false);
        setWarning(false);
    };

    const handleClearAll = () => {
        lastProcessedEanRef.current = "";
        dispatch({ type: 'clear' });
        setError(false);
        setSuccess(false);
        setWarning(false);
    };

    const canPrintProduct = () => {
        if (
            state.idClient === 0 ||
            state.idProductMotul === 0 ||
            state.labelQuantity <= 0 ||
            state.idLayoutEtiqueta === 0 ||
            state.idPrinter === 0
        ) {
            setMessage(t("fillRequiredFields"));
            setError(true);
            return false;
        }

        setError(false);
        return true;
    };

    const handlePrintMotulLabel = () => {
        if (!canPrintProduct()) {
            return;
        }

        callUpdateMotulLabelData()
            .then((labelDataAtualizado) => {
                const request = {
                    ...state,
                    ...labelDataAtualizado
                };

                LabelScanningService
                    .insertLabelMotul(request)
                    .then(() => {
                        setMessage(t("printSuccess"));
                        dispatch({ type: 'success' });
                        setSuccess(true);
                        handleClearAll();
                    })
                    .catch((e) => {
                        console.log(e);
                        setMessage(t("printError"));
                        dispatch({ type: 'error' });
                        setError(true);
                        setSuccess(false);
                    });
            })
            .catch((e) => {
                console.log(e);
                setMessage(t("printError"));
                setError(true);
                setSuccess(false);
            });
    };

    const callUpdateMotulLabelData = () => {
        const request = {
            ...state
        };

        return LabelScanningService
            .getLabelMotulData(request)
            .then((response) => {
                const updated = updateMotulLabelData(response.data);
                return updated;
            });
    };

    const updateMotulLabelData = (labelMotulData) => {
        if (!labelMotulData.zplTemplate) {
            throw new Error("Template ZPL Motul nao retornado pelo backend.");
        }

        const zpl = replaceZplVariables(labelMotulData);

        const updated = {
            productName: !labelMotulData.productName?.trim() ? "-" : labelMotulData.productName,
            productCode: labelMotulData.productCode ?? "",
            labelInformation: labelMotulData.labelInformation ?? "",
            idLayoutEtiqueta: labelMotulData.idLayoutEtiqueta,
            idPrinter: state.idPrinter,
            label: zpl
        };

        dispatch({ type: 'update', data: updated });

        return updated;
    };

    const replaceZplVariables = (labelMotulData) => {
        const zpl = labelMotulData.zplTemplate
            .replace(/#Nome_Prod#/g, labelMotulData.productName ?? "")
            .replace(/#Info#/g, labelMotulData.labelInformation ?? "")
            .replace(/#End_Fabricante#/g, labelMotulData.manufacturerAddress ?? "")
            .replace(/#Nome_Cliente#/g, labelMotulData.clientName ?? "")
            .replace(/#CNPJ#/g, labelMotulData.cnpj ?? "")
            .replace(/#Endereco_Cliente#/g, labelMotulData.clientAddress ?? "")
            .replace(/#Telefone_Cliente#/g, labelMotulData.clientPhone ?? "")
            .replace(/#Fabricante#/g, labelMotulData.manufacturerName ?? "")
            .replace(/#Resp_Tecnico#/g, labelMotulData.technicalResponsible ?? "");

        return zpl;
    };

    return (
        <>
            <DefaultScreenAccordion
                isConsult
                title="labelScanning"
                fields={
                    <>
                        <GridItemContainer>
                            <CustomAlert
                                error={error}
                                success={success}
                                warning={warning}
                                message={message}
                                handleCloseAlert={handleCloseAlertProduct}
                                snackbar
                                autoClose
                            />

                            <GridInputRow>
                                <Grid item xs={12} sm={6} md={2}>
                                    <div style={{
                                        pointerEvents: "none",
                                        opacity: 0.8
                                    }}>
                                        <DefaultAutocomplete
                                            iconLabelSwitch={
                                                <IconLabelSwitch
                                                    name="unLock"
                                                    labelName="client"
                                                />
                                            }
                                            name="idClient"
                                            disabled={true}
                                            value={state.idClient}
                                            defaultSize
                                            useApi
                                            endpoint="GetClient"
                                            error={error && state.idClient === 0}
                                        />
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultAutocomplete
                                        iconLabelSwitch={
                                            <IconLabelSwitch
                                                name="idProductMotul"
                                                labelName="productMotul"
                                            />
                                        }
                                        onChange={handleChange}
                                        name="idProductMotul"
                                        value={state.idProductMotul}
                                        defaultSize
                                        useApi
                                        endpoint="GetProductMotul"
                                        error={error && state.idProductMotul === 0}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <InputField
                                        iconLabelSwitch={
                                            <IconLabelSwitch name="code" labelName="productCodeEAN" />
                                        }
                                        name="productCodeEAN"
                                        control={control}
                                        defaultValue=""
                                        type="text"
                                        onChange={handleChange}
                                        onBlur={handleProductEanBlur}
                                        onKeyPress={handleProductEanKeyPress}
                                        value={state.productCodeEAN}
                                        error={error && state.productCodeEAN === ""}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <InputField
                                        name="labelQuantity"
                                        control={control}
                                        type="number"
                                        defaultValue=""
                                        onChange={handleChange}
                                        value={state.labelQuantity}
                                        length={30}
                                        error={error && Number(state.labelQuantity) <= 0}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <DefaultAutocomplete
                                        iconLabelSwitch={
                                            <IconLabelSwitch
                                                name="layout"
                                                labelName="layoutMotul"
                                            />
                                        }
                                        onChange={handleChange}
                                        name="idLayoutEtiqueta"
                                        value={state.idLayoutEtiqueta}
                                        defaultSize
                                        useApi
                                        endpoint="GetLayoutEtiquetaMotul"
                                        endPointData={state.idClient}
                                        error={error && state.idLayoutEtiqueta === 0}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <PrinterClientAutocomplete
                                        name="idPrinter"
                                        onChange={handleChange}
                                        value={state.idPrinter}
                                        label={t("printer")}
                                        defaultSize
                                        useApi
                                        endPointData={{ idClient: state.idClient }}
                                        endpoint="GetPrinterClient"
                                        error={error && state.idPrinter === 0}
                                    />
                                </Grid>
                            </GridInputRow>

                            {alert && (
                                <DialogConformationGeneric
                                    open={alert}
                                    message="productCannotBeLabeled"
                                    handleClose={alertCallback}
                                    hideCancel={true}
                                    maxWidth={'xs'}
                                />
                            )}

                            <Grid item xs={4} sm={3} md={2}>
                                <DefaultButton
                                    print
                                    className="primaryButton"
                                    onClick={handlePrintMotulLabel}
                                />
                            </Grid>

                            <Grid item xs={4} sm={3} md={2}>
                                <DefaultButton
                                    clear
                                    className="secondaryButton"
                                    onClick={handleClearAll}
                                />
                            </Grid>
                        </GridItemContainer>
                    </>
                }
            />
        </>
    );
};